import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useFieldCfg } from "@/lib/softwareConfig";
import { CustomerCombobox, ProductCombobox } from "@/components/InvoiceCombos";
import { GstBreakdown } from "@/components/GstBreakdown";
import { queueDraft } from "@/lib/offlineDrafts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateInvoice() {
  const fcfg = useFieldCfg("sales_invoice");
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "",
    gst_type: "cgst_sgst",
    notes: "",
    status: "unpaid",
    delivery_note: "",
    vehicle_no: "",
    eway_bill_no: "",
    ship_to_name: "",
    ship_to_address: "",
    ship_to_city: "",
    ship_to_state: "",
    ship_to_pincode: "",
    ship_to_gstin: "",
    show_payment_status: null,
  });
  const [shipToOpen, setShipToOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const [sellerStateCode, setSellerStateCode] = useState("");

  const fetchData = async () => {
    try {
      const [customersRes, productsRes, nextNoRes, settingsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/invoices/next-number`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
      ]);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setFormData((prev) => ({ ...prev, invoice_no: nextNoRes.data.invoice_no }));
      const s = settingsRes.data || {};
      const code = (s.state_code || (s.gstin || "").slice(0, 2) || "").toString();
      setSellerStateCode(code);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Auto-derive GST type from seller (settings) vs buyer (customer) state code.
  // User can still manually override in the dropdown.
  const autoGstType = (buyerStateCode) => {
    const a = (sellerStateCode || "").trim().padStart(2, "0").slice(0, 2);
    const b = (buyerStateCode || "").trim().padStart(2, "0").slice(0, 2);
    if (!a || !b || a === "00" || b === "00") return null;
    return a === b ? "cgst_sgst" : "igst";
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    const derived = autoGstType(customer.state_code);
    setFormData((prev) => ({
      ...prev,
      customer_id: customer.id,
      // don't override if user already picked "no_gst" (Tax-Free)
      gst_type: prev.gst_type === "no_gst" ? "no_gst" : (derived || prev.gst_type),
    }));
  };

  const handleProductSelect = (index, product) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      product_name: product.name,
      hsn_sac: product.hsn_sac,
      price: product.price,
      gst_rate: product.gst_rate,
      unit: product.unit || "PCS",
      amount_overridden: false,
    };
    newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(product.price) || 0);
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product_id: "", product_name: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 18, amount: 0 }]);
  };

  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const product = products.find((p) => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].hsn_sac = product.hsn_sac;
        newItems[index].price = product.price;
        newItems[index].gst_rate = product.gst_rate;
        newItems[index].unit = product.unit || "PCS";
        newItems[index].amount_overridden = false;
      }
    }
    if (field === "amount") {
      newItems[index].amount = parseFloat(value) || 0;
      newItems[index].amount_overridden = true;
    } else if ((field === "quantity" || field === "price") && !newItems[index].amount_overridden) {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].price) || 0);
    }
    setItems(newItems);
  };

  const resetAmount = (index) => {
    const newItems = [...items];
    newItems[index].amount_overridden = false;
    newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].price) || 0);
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    let cgst = 0, sgst = 0, igst = 0;
    // "No GST" bills skip tax entirely — cgst/sgst/igst all stay zero.
    if (formData.gst_type !== "no_gst") {
      const totalGst = items.reduce((sum, item) => sum + (item.amount * item.gst_rate) / 100, 0);
      if (formData.gst_type === "cgst_sgst") { cgst = totalGst / 2; sgst = totalGst / 2; }
      else { igst = totalGst; }
    }
    return { subtotal, cgst, sgst, igst, total: formData.rcm ? subtotal : subtotal + cgst + sgst + igst };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) { alert("Please select a customer"); return; }
    if (items.length === 0) { alert("Please add at least one item"); return; }
    if (items.some((it) => !it.product_id)) { alert("Please select a product for every item"); return; }
    try {
      const response = await axios.post(`${API}/invoices`, { ...formData, items });
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      if (!error.response || !navigator.onLine) {
        // Network failure — save as offline draft; it auto-syncs when internet returns.
        queueDraft({ ...formData, items });
        alert("No internet — the invoice was saved as an OFFLINE DRAFT. It will be created automatically once you are back online. ✓");
        navigate("/invoices");
        return;
      }
      console.error("Error creating invoice:", error);
      alert(error?.response?.data?.detail || "Failed to create invoice. Please try again.");
    }
  };

  const totals = calculateTotals();

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="create-invoice-page">
      <div className="mb-8">
        <button onClick={() => navigate("/invoices")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors duration-200">
          <ArrowLeft size={20} />
          <span className="text-sm font-semibold">Back to Invoices</span>
        </button>
        <h1 className="text-4xl font-black tracking-tighter text-slate-900 mb-2">Create New Invoice</h1>
        <p className="text-slate-500 text-sm">Fill in the details below</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Details */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Invoice Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice_no">Invoice Number *</Label>
                  <Input id="invoice_no" data-testid="invoice-number-input" value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="invoice_date">{fcfg.label("invoice_date", "Invoice Date")} *</Label>
                  <Input id="invoice_date" type="date" data-testid="invoice-date-input" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} disabled={!fcfg.canEdit("invoice_date")} required />
                </div>
                <div>
                  <Label htmlFor="vehicle_no_top">{fcfg.label("vehicle_no", "Motor Vehicle No")}</Label>
                  <Input id="vehicle_no_top" data-testid="vehicle-no-input-top" value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} disabled={!fcfg.canEdit("vehicle_no")} placeholder="e.g. MH 01 AB 1234" />
                </div>
                <div>
                  <Label htmlFor="due_date">{fcfg.label("due_date", "Due Date")}</Label>
                  <Input id="due_date" type="date" data-testid="invoice-due-date-input" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} disabled={!fcfg.canEdit("due_date")} />
                </div>
                <div>
                  <Label htmlFor="customer">Customer *</Label>
                  <CustomerCombobox
                    customers={customers}
                    value={formData.customer_id}
                    onSelect={handleCustomerSelect}
                    onCreated={(c) => { setCustomers((prev) => [...prev, c]); handleCustomerSelect(c); }}
                    testId="select-customer"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="gst_type">{fcfg.label("gst_type", "GST Type")} *</Label>
                  <select id="gst_type" data-testid="gst-type-select" value={formData.gst_type} onChange={(e) => setFormData({ ...formData, gst_type: e.target.value })} disabled={!fcfg.canEdit("gst_type")} className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold disabled:bg-slate-100 disabled:text-slate-400" required>
                    <option value="cgst_sgst">CGST + SGST (Intra-State)</option>
                    <option value="igst">IGST (Inter-State)</option>
                    <option value="no_gst">No GST / Nil Rated (Tax-Free Bill)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-slate-50">
                    <div>
                      <p className="text-sm font-bold text-slate-900">Reverse Charge Mechanism (RCM)</p>
                      <p className="text-xs text-slate-500">Tax payable by the buyer/recipient — invoice total = taxable amount</p>
                    </div>
                    <Switch checked={formData.rcm} onCheckedChange={(v) => setFormData({ ...formData, rcm: v })} disabled={!fcfg.canEdit("rcm")} data-testid="rcm-toggle" />
                  </div>
                </div>
              </div>
              {selectedCustomer && (
                <div className="mt-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Billing To</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedCustomer.name}</p>
                  <p className="text-sm text-slate-600">{selectedCustomer.address}, {selectedCustomer.city}</p>
                  <p className="text-sm text-slate-600">{selectedCustomer.state} - {selectedCustomer.pincode}</p>
                  {selectedCustomer.gstin && <p className="text-sm text-slate-600 font-mono mt-1">GSTIN: {selectedCustomer.gstin}</p>}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900">Items</h2>
                <button type="button" onClick={addItem} data-testid="add-item-btn" className="px-3 py-2 bg-blue-600 text-white rounded-sm text-sm font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center gap-2">
                  <Plus size={16} weight="bold" /> Add Item
                </button>
              </div>
              {items.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-sm">
                  <p className="text-slate-500 mb-4">No items added yet</p>
                  <button type="button" onClick={addItem} className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200">Add First Item</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} data-testid={`invoice-item-${index}`} className="border border-slate-200 rounded-sm p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 grid grid-cols-5 gap-3">
                          <div className="col-span-2">
                            <Label>Product *</Label>
                            <ProductCombobox
                              products={products}
                              value={item.product_id}
                              onSelect={(p) => handleProductSelect(index, p)}
                              onCreated={(p) => setProducts((prev) => [...prev, p])}
                              testId={`item-product-select-${index}`}
                            />
                            {/* Live stock pill — sits next to the picked product so the user can see availability while keying in quantity. */}
                            {(() => {
                              const p = products.find((x) => x.id === item.product_id);
                              if (!p) return null;
                              const stock = p.current_stock ?? 0;
                              const reorder = p.reorder_level ?? 0;
                              const afterSale = stock - (parseFloat(item.quantity) || 0);
                              let cls = "bg-emerald-100 text-emerald-700";
                              let icon = "●";
                              if (afterSale < 0) { cls = "bg-red-100 text-red-700"; icon = "⚠"; }
                              else if (stock <= reorder && reorder > 0) { cls = "bg-amber-100 text-amber-800"; icon = "▲"; }
                              return (
                                <div data-testid={`item-stock-badge-${index}`} className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}>
                                  <span>{icon}</span>
                                  <span>Available: {stock} {p.unit || "PCS"}</span>
                                  {afterSale < 0 && (
                                    <span className="ml-1 font-bold uppercase">Will go negative ({afterSale.toFixed(2)})</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div>
                            <Label>Quantity *</Label>
                            <Input type="number" step="0.01" min="0.01" data-testid={`item-quantity-${index}`} value={item.quantity} onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)} required />
                          </div>
                          <div>
                            <Label>Price</Label>
                            <Input type="number" step="0.01" data-testid={`item-price-${index}`} value={item.price} onChange={(e) => updateItem(index, "price", parseFloat(e.target.value) || 0)} required />
                          </div>
                          <div>
                            <div className="flex items-center justify-between gap-1">
                              <Label className="flex items-center gap-1.5">
                                Amount
                                {item.amount_overridden && (
                                  <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700" title="Manually overridden — auto-calc disabled">Overridden</span>
                                )}
                              </Label>
                              {item.amount_overridden && (
                                <button
                                  type="button"
                                  onClick={() => resetAmount(index)}
                                  data-testid={`item-amount-reset-${index}`}
                                  className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                                  title="Restore auto-calculated amount (qty × price)"
                                >Reset</button>
                              )}
                            </div>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              data-testid={`item-amount-${index}`}
                              value={item.amount}
                              onChange={(e) => updateItem(index, "amount", e.target.value)}
                              className={item.amount_overridden ? "border-amber-400 bg-amber-50/40" : ""}
                            />
                          </div>
                        </div>
                        <button type="button" onClick={() => removeItem(index)} data-testid={`remove-item-${index}`} className="mt-6 p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200">
                          <Trash size={18} />
                        </button>
                      </div>
                      {item.hsn_sac && (
                        <div className="mt-2 flex gap-4 text-xs text-slate-500">
                          <span>HSN/SAC: {item.hsn_sac}</span>
                          <span>GST: {item.gst_rate}%</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Transport / Delivery Details */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Transport & Delivery</h2>
              <p className="text-xs text-slate-500 mb-4">Optional fields shown on invoice PDF</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="delivery_note">Delivery Note</Label>
                  <Input id="delivery_note" autoCap data-testid="delivery-note-input" value={formData.delivery_note} onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })} placeholder="e.g. DN-2026-001" />
                </div>
                <div>
                  <Label htmlFor="vehicle_no">Motor Vehicle No</Label>
                  <Input id="vehicle_no" data-testid="vehicle-no-input" value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} placeholder="e.g. MH 01 AB 1234" />
                </div>
                <div>
                  <Label htmlFor="eway_bill_no">E-Way Bill No</Label>
                  <Input id="eway_bill_no" data-testid="eway-bill-input" value={formData.eway_bill_no} onChange={(e) => setFormData({ ...formData, eway_bill_no: e.target.value })} placeholder="e.g. 123456789012" />
                </div>
              </div>
            </div>

            {/* Ship To (Optional) */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-bold text-slate-900">Ship To (Optional)</h2>
                <button type="button" onClick={() => setShipToOpen(v => !v)} data-testid="ship-to-toggle" className="text-xs text-blue-600 font-semibold hover:underline">{shipToOpen ? "Hide" : "Add shipping address"}</button>
              </div>
              <p className="text-xs text-slate-500 mb-4">If filled, a separate "Ship To" column will appear on the PDF beside Bill To. Leave blank for a single-column layout.</p>
              {shipToOpen && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Recipient Name</Label><Input data-testid="ship-to-name" autoCap value={formData.ship_to_name} onChange={(e) => setFormData({...formData, ship_to_name: e.target.value})} /></div>
                  <div><Label>GSTIN</Label><Input data-testid="ship-to-gstin" value={formData.ship_to_gstin} onChange={(e) => setFormData({...formData, ship_to_gstin: e.target.value.toUpperCase()})} /></div>
                  <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="ship-to-address" autoCap value={formData.ship_to_address} onChange={(e) => setFormData({...formData, ship_to_address: e.target.value})} /></div>
                  <div><Label>City</Label><Input data-testid="ship-to-city" autoCap value={formData.ship_to_city} onChange={(e) => setFormData({...formData, ship_to_city: e.target.value})} /></div>
                  <div><Label>State</Label><Input data-testid="ship-to-state" autoCap value={formData.ship_to_state} onChange={(e) => setFormData({...formData, ship_to_state: e.target.value})} /></div>
                  <div><Label>Pincode</Label><Input data-testid="ship-to-pincode" value={formData.ship_to_pincode} onChange={(e) => setFormData({...formData, ship_to_pincode: e.target.value})} /></div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">{fcfg.label("notes", "Notes")}</h2>
              <Textarea data-testid="invoice-notes" autoCap value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} disabled={!fcfg.canEdit("notes")} placeholder="Add any additional notes or terms..." rows={4} />
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Summary</h2>
              <div className="mb-6 pb-4 border-b border-slate-200">
                <span data-testid="gst-type-badge" className={`inline-flex px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider ${
                  formData.gst_type === "cgst_sgst" ? "bg-blue-100 text-blue-800"
                  : formData.gst_type === "no_gst" ? "bg-slate-200 text-slate-700"
                  : "bg-purple-100 text-purple-800"}`}>
                  {formData.gst_type === "cgst_sgst" ? "CGST + SGST"
                   : formData.gst_type === "no_gst" ? "NO GST"
                   : "IGST"}
                </span>
                {formData.rcm && (
                  <span data-testid="rcm-badge" className="inline-flex ml-2 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider bg-red-100 text-red-800">RCM</span>
                )}
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold text-slate-900">{"\u20b9"}{totals.subtotal.toFixed(2)}</span>
                </div>
                {formData.gst_type === "no_gst" ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 italic">Tax</span>
                    <span className="font-semibold text-slate-500 italic">Not applicable</span>
                  </div>
                ) : (
                  <GstBreakdown items={items} gstType={formData.gst_type} />
                )}
                {formData.rcm && formData.gst_type !== "no_gst" && (
                  <div className="flex justify-between text-xs bg-red-50 border border-red-200 rounded-sm p-2" data-testid="rcm-info-line">
                    <span className="text-red-700 font-semibold">Tax payable by recipient (RCM)</span>
                    <span className="font-bold text-red-700">{"\u20b9"}{(totals.cgst + totals.sgst + totals.igst).toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-slate-900">{formData.rcm ? "Total (Taxable)" : "Total"}</span>
                    <span className="text-2xl font-black text-slate-900">{"\u20b9"}{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Button type="submit" data-testid="create-invoice-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold">Create Invoice</Button>
                <Button type="button" variant="outline" onClick={() => navigate("/invoices")} className="w-full">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
